import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { MdClose, MdHome, MdLogin, MdMenu, MdSettings } from 'react-icons/md';
import { IoGrid } from 'react-icons/io5';
import { FaCrown } from 'react-icons/fa6';
import { FaPlay, FaRegBookmark } from 'react-icons/fa';

import { useRouter } from 'next/router';

import { useTheme } from 'styled-components';

import { useAuth } from '~/common/providers/auth';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { UserTypesEnum } from '~/common/enums/user-types.enum';
import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { Logo } from '~/common/components/Logo';
import { Button } from '~/common/components/Button';

import {
  ActiveNavIndicator,
  CtaButtonWrapper,
  HeaderContainer,
  LoggedInWrapper,
  MobileMenuBackdrop,
  MobileMenuButtonWrapper,
  MobileMenuContent,
  MobileMenuPanel,
  NavItemsContainer,
  StyWrapper,
} from './styles';
import { SubscribePlansModal } from './components/SubscribePlansModal';

export const Header: FC = () => {
  const theme = useTheme();
  const router = useRouter();
  const { loggedIn, user: { type: userType, subscription } = {} } = useAuth();
  const isAdmin = useMemo(() => userType === UserTypesEnum.ADMIN, [userType]);
  const showSubscribeButton = useMemo(
    () => loggedIn && subscription === UserSubscriptionTypesEnum.FREE,
    [loggedIn, subscription]
  );

  const smallScreen = useMediaQuery(
    `(max-width: ${
      isAdmin
        ? showSubscribeButton
          ? 860
          : 770
        : !loggedIn
        ? 300
        : showSubscribeButton
        ? 860
        : 760
    }px)`
  );
  const verySmallScreen = useMediaQuery(
    `(max-width: ${isAdmin ? 430 : showSubscribeButton ? 470 : 430}px)`
  );

  const [adminMobileMenuOpen, setAdminMobileMenuOpen] = useState(false);
  const [subscribePlansModalOpen, setSubscribePlansModalOpen] = useState(false);

  const activeRoute = useMemo(() => {
    const path = router.asPath;
    if (path.startsWith('/admin')) return 'admin';
    if (path.startsWith('/saved')) return 'saved';
    if (path.startsWith('/account')) return 'account';
    if (path.startsWith('/prompts')) return 'prompts';
    if (path.startsWith('/training')) return 'training';
    return null;
  }, [router.asPath]);

  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });

  const getNavItemColor = useCallback(
    (route: string) => (activeRoute === route ? theme.primary : theme.neutral0),
    [activeRoute, theme.primary, theme.neutral0]
  );

  const showAdminPageButtons = useMemo(
    () => userType === UserTypesEnum.ADMIN,
    [userType]
  );

  const showCompactAdminMenu = useMemo(
    () => verySmallScreen,
    [verySmallScreen]
  );

  const loginHref = useMemo(() => {
    const isPromptDetailsRoute = /^\/prompts\/[^/?#]+/.test(router.asPath);

    if (!isPromptDetailsRoute) {
      return '/login';
    }

    return `/login?redirect=${encodeURIComponent(router.asPath)}`;
  }, [router.asPath]);

  useEffect(() => {
    setAdminMobileMenuOpen(false);
  }, [router.asPath]);

  useEffect(() => {
    if (!showCompactAdminMenu) {
      setAdminMobileMenuOpen(false);
    }
  }, [showCompactAdminMenu]);

  useEffect(() => {
    if (!activeRoute || showCompactAdminMenu) {
      setIndicatorStyle({ width: 0, left: 0 });
      return;
    }

    const el = document.getElementById(`nav-item-${activeRoute}`);
    if (el) {
      setIndicatorStyle({
        width: el.offsetWidth,
        left: el.offsetLeft,
      });
      return;
    }

    setIndicatorStyle({ width: 0, left: 0 });
  }, [activeRoute, smallScreen, showCompactAdminMenu, showAdminPageButtons]);

  return (
    <>
      <SubscribePlansModal
        isOpen={subscribePlansModalOpen}
        onClose={() => setSubscribePlansModalOpen(false)}
      />

      <HeaderContainer>
        <StyWrapper>
          <Logo />

          {loggedIn ? (
            <>
              <LoggedInWrapper>
                {showCompactAdminMenu ? (
                  <MobileMenuButtonWrapper>
                    {showSubscribeButton && (
                      <Button
                        text={verySmallScreen ? null : 'Assinar'}
                        icon={FaCrown}
                        onClick={() => setSubscribePlansModalOpen(true)}
                        height="40"
                        backgroundColor={theme.primary}
                        textOrIconColor={theme.neutral1000}
                        borderColor={theme.primary}
                        hoverBackgroundColor={theme.primary}
                      />
                    )}
                    <Button
                      text={verySmallScreen ? null : 'Menu'}
                      icon={adminMobileMenuOpen ? MdClose : MdMenu}
                      onClick={() =>
                        setAdminMobileMenuOpen((prevState) => !prevState)
                      }
                      height="40"
                      backgroundColor={theme.neutral950}
                      textOrIconColor={theme.neutral0}
                      borderColor={theme.transparent}
                      hoverBackgroundColor={theme.neutral800}
                    />
                  </MobileMenuButtonWrapper>
                ) : (
                  <NavItemsContainer>
                    <ActiveNavIndicator
                      style={{
                        width: `${indicatorStyle.width}px`,
                        left: `${indicatorStyle.left}px`,
                        opacity: indicatorStyle.width > 0 ? 1 : 0,
                      }}
                    />

                    {showAdminPageButtons && (
                      <div id="nav-item-admin">
                        <Button
                          text={smallScreen ? null : 'Admin'}
                          height="40"
                          icon={MdSettings}
                          href="/admin"
                          backgroundColor={theme.neutral950}
                          textOrIconColor={getNavItemColor('admin')}
                          borderColor={theme.transparent}
                          hoverBackgroundColor={theme.neutral800}
                        />
                      </div>
                    )}

                    {/* <div id="nav-item-training">
                      <Button
                        height="40"
                        icon={FaPlay}
                        href="/training"
                        backgroundColor={theme.neutral950}
                        textOrIconColor={getNavItemColor('training')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                        text={smallScreen ? null : 'Treinamentos'}
                      />
                    </div> */}

                    <div id="nav-item-saved">
                      <Button
                        height="40"
                        icon={FaRegBookmark}
                        href="/saved"
                        backgroundColor={theme.neutral950}
                        textOrIconColor={getNavItemColor('saved')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                        text={smallScreen ? null : 'Salvos'}
                      />
                    </div>

                    <div id="nav-item-prompts">
                      <Button
                        height="40"
                        icon={IoGrid}
                        href="/prompts"
                        backgroundColor={theme.neutral950}
                        textOrIconColor={getNavItemColor('prompts')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                        text={smallScreen ? null : 'Prompts'}
                      />
                    </div>

                    {loggedIn && (
                      <>
                        <div id="nav-item-account">
                          <Button
                            height="40"
                            icon={MdHome}
                            href="/account"
                            backgroundColor={theme.neutral950}
                            textOrIconColor={getNavItemColor('account')}
                            borderColor={theme.transparent}
                            hoverBackgroundColor={theme.neutral800}
                            text={smallScreen ? null : 'Conta'}
                          />
                        </div>
                      </>
                    )}

                    {showSubscribeButton && (
                      <CtaButtonWrapper>
                        <Button
                          text={smallScreen ? null : 'Assinar'}
                          icon={FaCrown}
                          onClick={() => setSubscribePlansModalOpen(true)}
                          height="40"
                          backgroundColor={theme.primary}
                          textOrIconColor={theme.neutral1000}
                          borderColor={theme.primary}
                          hoverBackgroundColor={theme.primary}
                        />
                      </CtaButtonWrapper>
                    )}
                  </NavItemsContainer>
                )}
              </LoggedInWrapper>

              {showCompactAdminMenu && (
                <>
                  <MobileMenuBackdrop
                    $open={adminMobileMenuOpen}
                    onClick={() => setAdminMobileMenuOpen(false)}
                  />

                  <MobileMenuPanel $open={adminMobileMenuOpen}>
                    <MobileMenuContent $open={adminMobileMenuOpen}>
                      <Button
                        height="40"
                        width="100%"
                        justifyContent="start"
                        icon={IoGrid}
                        href="/prompts"
                        backgroundColor={theme.neutral900}
                        textOrIconColor={getNavItemColor('prompts')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                        text="Prompts"
                      />

                      {/* <Button
                        text="Treinamentos"
                        width="100%"
                        justifyContent="start"
                        height="40"
                        icon={FaPlay}
                        href="/training"
                        onClick={() => setAdminMobileMenuOpen(false)}
                        backgroundColor={theme.neutral900}
                        textOrIconColor={getNavItemColor('training')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                      /> */}

                      <Button
                        text="Salvos"
                        width="100%"
                        justifyContent="start"
                        height="40"
                        icon={FaRegBookmark}
                        href="/saved"
                        onClick={() => setAdminMobileMenuOpen(false)}
                        backgroundColor={theme.neutral900}
                        textOrIconColor={getNavItemColor('saved')}
                        borderColor={theme.transparent}
                        hoverBackgroundColor={theme.neutral800}
                      />

                      {showAdminPageButtons && (
                        <Button
                          text="Admin"
                          width="100%"
                          justifyContent="start"
                          height="40"
                          icon={MdSettings}
                          href="/admin"
                          onClick={() => setAdminMobileMenuOpen(false)}
                          backgroundColor={theme.neutral900}
                          textOrIconColor={getNavItemColor('admin')}
                          borderColor={theme.transparent}
                          hoverBackgroundColor={theme.neutral800}
                        />
                      )}

                      {loggedIn && (
                        <Button
                          text="Conta"
                          justifyContent="start"
                          width="100%"
                          height="40"
                          icon={MdHome}
                          href="/account"
                          backgroundColor={theme.neutral900}
                          textOrIconColor={getNavItemColor('account')}
                          borderColor={theme.transparent}
                          hoverBackgroundColor={theme.neutral800}
                        />
                      )}
                    </MobileMenuContent>
                  </MobileMenuPanel>
                </>
              )}
            </>
          ) : (
            <Button
              text={smallScreen ? null : 'Entrar'}
              icon={MdLogin}
              href={loginHref}
              height="40"
              backgroundColor={theme.neutral900}
              textOrIconColor={theme.primary}
              borderColor={theme.transparent}
              hoverBackgroundColor={theme.neutral800}
            />
          )}
        </StyWrapper>
      </HeaderContainer>
    </>
  );
};
